/**
 * Bilingual (EN/BG) user guide content: "How to write a scientific article with InkPhD".
 * Pure data — rendered by src/pages/GuidePage.jsx.
 */

export const GUIDE_UI = {
  en: {
    badge: 'User guide',
    title: 'How to write a scientific article with InkPhD',
    subtitle:
      'The complete workflow, from an empty project to a submission-ready manuscript with '
      + 'verified Vancouver citations. Follow the steps in order the first time; afterwards you '
      + 'can jump straight to the part you need.',
    quickstart: 'Quick start in 30 minutes',
    quickstartLead:
      'The shortest path to a first draft grounded in real literature. Each step links to the '
      + 'detailed section below.',
    contents: 'Contents',
    step: 'Step',
    tip: 'Tip',
    print: 'Download / print as PDF',
    lang: 'Language',
    backToApp: 'Back to my projects',
    footNote:
      'InkPhD is research-use-only writing infrastructure. Every AI-generated passage must be '
      + 'read, verified and approved by you before it enters a submission.',
  },
  bg: {
    badge: 'Ръководство',
    title: 'Как се пише научна статия с InkPhD',
    subtitle:
      'Пълният работен процес — от празен проект до готов за изпращане ръкопис с проверени '
      + 'ванкувърски цитати. Първия път минете стъпките по ред; после можете да отваряте само '
      + 'частта, която ви трябва.',
    quickstart: 'Бърз старт за 30 минути',
    quickstartLead:
      'Най-краткият път до първа чернова, стъпила върху реална литература. Всяка стъпка води '
      + 'към подробния раздел по-долу.',
    contents: 'Съдържание',
    step: 'Стъпка',
    tip: 'Съвет',
    print: 'Изтегли / принтирай като PDF',
    lang: 'Език',
    backToApp: 'Обратно към проектите',
    footNote:
      'InkPhD е инфраструктура за писане с изследователско предназначение. Всеки генериран от '
      + 'ИИ текст трябва да бъде прочетен, проверен и одобрен от вас, преди да влезе в ръкопис '
      + 'за публикуване.',
  },
};

export const GUIDE_QUICKSTART = {
  en: [
    { to: '#project', t: 'Create the project', d: 'Pick the document type, title and output language (3 min).' },
    { to: '#library', t: 'Add 10–20 sources', d: 'Upload PDFs or import from PubMed; wait for indexing (10 min).' },
    { to: '#structure', t: 'Generate the structure', d: 'Let the AI propose sections, then edit the list (5 min).' },
    { to: '#generate', t: 'Write one section', d: 'Use Fast mode for the first pass (5 min).' },
    { to: '#citations', t: 'Check the citations', d: 'Re-verify and read the numbered reference list (3 min).' },
    { to: '#readiness', t: 'Run Readiness', d: 'See exactly what is missing before you go further (4 min).' },
  ],
  bg: [
    { to: '#project', t: 'Създайте проекта', d: 'Изберете тип документ, заглавие и език на текста (3 мин).' },
    { to: '#library', t: 'Добавете 10–20 източника', d: 'Качете PDF-и или внесете от PubMed; изчакайте индексирането (10 мин).' },
    { to: '#structure', t: 'Генерирайте структурата', d: 'ИИ предлага секции, вие ги редактирате (5 мин).' },
    { to: '#generate', t: 'Напишете една секция', d: 'За първата чернова ползвайте Fast mode (5 мин).' },
    { to: '#citations', t: 'Проверете цитатите', d: 'Пуснете реверификация и прочетете номерирания списък (3 мин).' },
    { to: '#readiness', t: 'Пуснете Readiness', d: 'Вижте точно какво липсва, преди да продължите (4 мин).' },
  ],
};

export const GUIDE_SECTIONS = [
  {
    id: 'project',
    icon: 'FilePlus2',
    title: { en: 'Create a project and choose the document type', bg: 'Създаване на проект и избор на тип документ' },
    lead: {
      en: 'Everything in InkPhD lives inside a project: its own evidence library, its own vector index, its own sections and exports. The document type changes the prompts, the default structure and the length targets, so choose it carefully.',
      bg: 'Всичко в InkPhD живее в рамките на проект: собствена библиотека с източници, собствен векторен индекс, собствени секции и експорти. Типът документ променя промптите, структурата по подразбиране и целевата дължина, затова го изберете внимателно.',
    },
    steps: {
      en: [
        'On the dashboard press "New project" to open the 3-step wizard.',
        'Step 1 — pick one of the 10 types: Doctoral dissertation, Research article, Systematic review, Meta-analysis, Clinical protocol, Grant proposal, Book/Monograph, Case report, Conference abstract, Editorial.',
        'Step 2 — enter the working title and a description of at least 30 characters (topic, population, design, what you want to show). This description feeds every later AI call, so be specific.',
        'Step 3 — choose the writing language (12 languages supported). The interface stays in English; only the generated text follows your choice.',
        'Dissertation and Book use a section-by-section workflow; all other types also offer a one-shot whole-article generator.',
      ],
      bg: [
        'В таблото натиснете „New project“, за да отворите съветника от 3 стъпки.',
        'Стъпка 1 — изберете един от 10-те типа: докторска дисертация, научна статия, систематичен обзор, мета-анализ, клиничен протокол, проектно предложение, книга/монография, клиничен случай, конферентно резюме, редакционна статия.',
        'Стъпка 2 — въведете работно заглавие и описание от поне 30 знака (тема, популация, дизайн, какво искате да покажете). Това описание се подава на всяко следващо ИИ действие, затова бъдете конкретни.',
        'Стъпка 3 — изберете езика на текста (поддържат се 12 езика). Интерфейсът остава на английски; само генерираният текст следва избора ви.',
        'Дисертацията и книгата работят секция по секция; при останалите типове има и генератор на цялата статия наведнъж.',
      ],
    },
    tip: {
      en: 'A vague description ("about pancreatitis") produces vague text. Write the description as if you were briefing a co-author who knows the field but not your study.',
      bg: 'Мъгливо описание („за панкреатит“) дава мъглив текст. Напишете описанието така, както бихте инструктирали съавтор, който познава полето, но не и вашето изследване.',
    },
  },
  {
    id: 'guideline',
    icon: 'ClipboardCheck',
    title: { en: 'Choose the reporting guideline (EQUATOR)', bg: 'Избор на отчетна насока (EQUATOR)' },
    lead: {
      en: 'InkPhD ships 11 EQUATOR guidelines plus a composite Dissertation checklist — 426 checklist items in total. The primary guideline drives the compliance engine and the auto-fix suggestions.',
      bg: 'InkPhD съдържа 11 EQUATOR насоки плюс композитен чеклист за дисертация — общо 426 точки. Основната насока задвижва двигателя за съответствие и автоматичните предложения за поправка.',
    },
    steps: {
      en: [
        'In step 3 of the wizard the AI proposes the top-3 guidelines with a fit score and a short rationale — pick the one that matches your design.',
        'Later you can change it in the "Guidelines" tab of the workspace.',
        'Typical mapping: RCT → CONSORT · observational → STROBE · systematic review → PRISMA · prediction model → TRIPOD+AI · diagnostic accuracy → STARD · case report → CARE · protocol → SPIRIT · economics → CHEERS · animal study → ARRIVE.',
        'For a PhD thesis use the composite "Dissertation" guideline together with the guideline of each sub-study.',
      ],
      bg: [
        'В стъпка 3 на съветника ИИ предлага трите най-подходящи насоки с оценка за съвпадение и кратка обосновка — изберете тази, която отговаря на дизайна ви.',
        'По-късно можете да я смените в таб „Guidelines“ на работната среда.',
        'Типично съответствие: РКИ → CONSORT · обсервационно → STROBE · систематичен обзор → PRISMA · прогностичен модел → TRIPOD+AI · диагностична точност → STARD · клиничен случай → CARE · протокол → SPIRIT · икономически анализ → CHEERS · животински модел → ARRIVE.',
        'За докторска теза използвайте композитната насока „Dissertation“ заедно с насоката на всяко подизследване.',
      ],
    },
    tip: {
      en: 'Choosing the guideline early is what makes the later compliance score meaningful — it is not a formality.',
      bg: 'Ранният избор на насока прави по-късната оценка за съответствие смислена — това не е формалност.',
    },
  },
  {
    id: 'library',
    icon: 'Library',
    title: { en: 'Build the evidence library', bg: 'Изграждане на библиотеката с източници' },
    lead: {
      en: 'InkPhD never writes from memory. Every factual sentence is retrieved from documents you added, so the library is the single biggest driver of quality.',
      bg: 'InkPhD никога не пише „по памет“. Всяко фактологично твърдение се извлича от документи, които вие сте добавили — затова библиотеката е най-силният фактор за качеството.',
    },
    steps: {
      en: [
        '"Library" tab → upload PDFs (full texts, guidelines, your own protocols). Files are stored in object storage and indexed in the background; the chunk counter tells you when a document is ready.',
        '"PubMed" tab → advanced search with MeSH terms, study-type and date filters, free-full-text-only and sorting. Import the hits you want with one click; InkPhD tries to fetch the open-access full text (Unpaywall / OpenAlex).',
        'Zotero: in "Settings" add a read-only Zotero API key + library ID to sync an existing reference collection.',
        'Mark methodological favourites as "style reference" — their voice is used as an example, never as evidence.',
        'Aim for 15–40 well-chosen sources per section. Ten full texts beat a hundred abstracts.',
      ],
      bg: [
        'Таб „Library“ → качете PDF-и (пълни текстове, насоки, собствени протоколи). Файловете се пазят в обектно хранилище и се индексират на заден план; броячът на чънкове показва кога документът е готов.',
        'Таб „PubMed“ → разширено търсене с MeSH термини, филтри по тип изследване и дата, само свободен пълен текст и сортиране. Внасяте избраните резултати с едно кликване; InkPhD опитва да изтегли свободния пълен текст (Unpaywall / OpenAlex).',
        'Zotero: в „Settings“ добавете read-only Zotero API ключ и номер на библиотека, за да синхронизирате съществуваща колекция.',
        'Отбележете методологичните си любимци като „style reference“ — техният стил служи за пример, но никога като доказателство.',
        'Целете 15–40 добре подбрани източника на секция. Десет пълни текста са по-добри от сто резюмета.',
      ],
    },
    tip: {
      en: 'Upload the full text, not the abstract. Retrieval works on paragraphs — an abstract gives the engine almost nothing to quote.',
      bg: 'Качвайте пълния текст, не резюмето. Извличането работи на нивото на абзаци — от резюме двигателят няма какво да цитира.',
    },
  },
  {
    id: 'question',
    icon: 'HelpCircle',
    title: { en: 'Research question, title, abstract, keywords', bg: 'Изследователски въпрос, заглавие, абстракт, ключови думи' },
    lead: {
      en: 'The "Research Question" tab holds the scientific spine of the manuscript. Every field has an inline "Generate" button that drafts a proposal grounded in your library and description.',
      bg: 'Таб „Research Question“ съдържа научния гръбнак на ръкописа. Всяко поле има вграден бутон „Generate“, който предлага чернова, стъпила върху библиотеката и описанието ви.',
    },
    steps: {
      en: [
        'Write (or generate, then edit) the research question in PICO form: population, intervention/exposure, comparator, outcome.',
        'Add the hypothesis, the objectives and the specialty — these sharpen every later generation.',
        'Generate title candidates and the abstract only after at least one results-bearing section exists, otherwise the abstract has nothing to summarise.',
        'Keywords: generate, then trim to the MeSH terms your target journal indexes.',
      ],
      bg: [
        'Напишете (или генерирайте и после редактирайте) изследователския въпрос в PICO форма: популация, интервенция/експозиция, компаратор, изход.',
        'Добавете хипотезата, целите и специалността — те правят всяко следващо генериране по-прецизно.',
        'Генерирайте заглавия и абстракт само след като има поне една секция с резултати, иначе абстрактът няма какво да резюмира.',
        'Ключови думи: генерирайте и после отсейте до MeSH термините, които вашето целево списание индексира.',
      ],
    },
    tip: {
      en: 'Treat the AI proposal as a first draft by a diligent junior colleague: keep the structure, replace the claims you cannot defend.',
      bg: 'Гледайте на предложението на ИИ като на чернова от старателен млад колега: запазете структурата, заменете твърденията, които не можете да защитите.',
    },
  },
  {
    id: 'structure',
    icon: 'ListTree',
    title: { en: 'Build the structure (AI Structure Builder or reference PDF)', bg: 'Изграждане на структурата (AI Structure Builder или референтен PDF)' },
    lead: {
      en: 'Instead of inventing a table of contents, let InkPhD propose one — or import the skeleton of a published thesis you admire.',
      bg: 'Вместо да измисляте съдържание от нула, оставете InkPhD да предложи — или внесете скелета на публикувана дисертация, която харесвате.',
    },
    steps: {
      en: [
        'In the "Writing" tab open the Structure Builder. Tab "AI Generate" proposes 6–12 sections with a description, rationale, subtopics and 4–6 PubMed-ready MeSH keywords per section (the keyword chips open PubMed directly).',
        'Tab "Import from Reference PDF" parses a published dissertation (≤30 MB) and rebuilds its hierarchy up to three levels — front matter such as Abstract/Acknowledgements/References is skipped.',
        'On the review screen reorder, rename, indent/outdent, delete or insert sections, then press "Apply".',
        'Later you can drag sections in the left pane; dropping onto the middle of a card nests it as a sub-section.',
      ],
      bg: [
        'В таб „Writing“ отворете Structure Builder. Таб „AI Generate“ предлага 6–12 секции с описание, обосновка, подтеми и 4–6 готови за PubMed MeSH ключови думи на секция (чиповете с думи отварят PubMed директно).',
        'Таб „Import from Reference PDF“ разчита публикувана дисертация (до 30 MB) и възстановява нейната иерархия до три нива — предни части като Abstract/Acknowledgements/References се пропускат.',
        'На екрана за преглед пренаредете, преименувайте, вложете/изнесете, изтрийте или вмъкнете секции и натиснете „Apply“.',
        'По-късно можете да влачите секциите в лявия панел; пускане в средата на карта я вгражда като подсекция.',
      ],
    },
    tip: {
      en: 'A good structure is the cheapest quality gain in the whole workflow — fix the outline before you spend generations on prose.',
      bg: 'Добрата структура е най-евтиното подобрение в целия процес — оправете плана, преди да изразходвате генерации за текст.',
    },
  },
  {
    id: 'section-library',
    icon: 'FolderTree',
    title: { en: 'Scope sources to each section', bg: 'Насочване на източниците към конкретна секция' },
    lead: {
      en: 'Retrieval quality collapses when a 300-source library is searched for every section. Attach the right subset to each section instead.',
      bg: 'Качеството на извличането пада, когато за всяка секция се търси в библиотека от 300 източника. Вместо това прикрепете правилното подмножество към всяка секция.',
    },
    steps: {
      en: [
        'Each section card shows a "Library · N" badge — click it to open the two-column dialog (Attached vs project pool).',
        'Attach the sources that belong to that section; a collapsible list shows documents already attached elsewhere so you can reuse them.',
        'With sources attached, generation is strictly limited to them. With none attached, InkPhD falls back to the unassigned project pool (you can disable that fallback).',
      ],
      bg: [
        'Всяка карта на секция показва бадж „Library · N“ — кликнете го, за да отворите диалога с две колони (прикрепени срещу общ пул на проекта).',
        'Прикрепете източниците, които принадлежат на тази секция; сгъваем списък показва документите, прикрепени към други секции, за да ги преизползвате.',
        'При прикрепени източници генерирането е строго ограничено до тях. Без прикрепени, InkPhD ползва неразпределения пул на проекта (може да изключите това).',
      ],
    },
    tip: {
      en: 'Methods sections work best with 5–10 very specific sources; discussion sections tolerate a wider set.',
      bg: 'Секциите „Методи“ работят най-добре с 5–10 много конкретни източника; дискусията понася по-широк набор.',
    },
  },
  {
    id: 'generate',
    icon: 'PenTool',
    title: { en: 'Generate a section', bg: 'Генериране на секция' },
    lead: {
      en: 'The generator writes an argued, citation-carrying draft — not filler. You control length, retrieval mode and speed.',
      bg: 'Генераторът пише аргументирана чернова с цитати, а не пълнеж. Вие управлявате дължината, режима на извличане и скоростта.',
    },
    steps: {
      en: [
        'Press "Write" on a section card (or "Generate section") and review the prompt: section title, your instruction, output language.',
        'Length slider: 1 000–8 000 words. The prompt enforces the floor, so 3 000 means 3 000 — expect 4–6 minutes for a long section.',
        'Retrieval mode: Dense (semantic), Hybrid (semantic + BM25 keyword, recommended) or BM25 (exact terminology). Optional LLM reranking for high-stakes passages.',
        'Fast mode (amber switch) halves the chunks and the word floor and uses a faster model — 30–90 seconds for a rough first pass.',
        'For non-dissertation types the Workspace dashboard also offers "Generate article" to produce title, abstract, all sections and references in one run.',
        'Generation runs in the background — you can keep reading or editing other sections while it finishes.',
      ],
      bg: [
        'Натиснете „Write“ върху карта на секция (или „Generate section“) и прегледайте заявката: заглавие на секцията, ваша инструкция, език на текста.',
        'Плъзгач за дължина: 1 000–8 000 думи. Промптът налага долната граница, така че 3 000 значи 3 000 — за дълга секция очаквайте 4–6 минути.',
        'Режим на извличане: Dense (семантичен), Hybrid (семантичен + BM25 по ключови думи, препоръчително) или BM25 (точна терминология). По желание — LLM преподреждане за особено важни пасажи.',
        'Fast mode (кехлибареният ключ) наполовинява чънковете и долната граница на думите и ползва по-бърз модел — 30–90 секунди за груба първа чернова.',
        'За типовете, различни от дисертация, таблото „Workspace“ предлага и „Generate article“ — заглавие, абстракт, всички секции и литература в едно пускане.',
        'Генерирането върви на заден план — можете да четете или редактирате други секции, докато завърши.',
      ],
    },
    tip: {
      en: 'Use Fast mode to test whether the structure and the sources work, then regenerate the section properly.',
      bg: 'Ползвайте Fast mode, за да проверите дали структурата и източниците работят, а после генерирайте секцията както трябва.',
    },
  },
  {
    id: 'editing',
    icon: 'Wand2',
    title: { en: 'Edit: ghost text, Smart Continue, selection toolbar', bg: 'Редактиране: ghost text, Smart Continue, тулбар при селекция' },
    lead: {
      en: 'The centre pane is a normal editor with three AI affordances that never overwrite your text without a preview.',
      bg: 'Централният панел е нормален редактор с три ИИ помощника, които никога не променят текста ви без предварителен преглед.',
    },
    steps: {
      en: [
        'Toggle "Live edit" on a section: after a ~1 second pause at the end of the text a grey suggestion appears — Tab accepts it, Esc dismisses it. Auto-save keeps the draft safe.',
        '"Continue here…" (toolbar) or the hover "+ add here" button on any paragraph opens Smart Continue: give a one-line instruction ("add two sentences contrasting Bakker 2012 with van Santvoort 2010"), choose 1–5 sentences, then Generate → preview → Accept or Reject.',
        'Select any text to get the floating toolbar: Expand · Shorten · Rewrite · Academic tone · Add citation · Translate. Citations in the selection are preserved.',
        'Version history is kept for every section, so you can always compare or roll back.',
      ],
      bg: [
        'Включете „Live edit“ за секция: след около 1 секунда пауза в края на текста се появява сиво предложение — Tab го приема, Esc го отхвърля. Автосъхранението пази черновата.',
        '„Continue here…“ (в тулбара) или бутонът „+ add here“ при посочване на абзац отваря Smart Continue: давате едноредова инструкция („добави две изречения, които съпоставят Bakker 2012 с van Santvoort 2010“), избирате 1–5 изречения, после Generate → преглед → Accept или Reject.',
        'Маркирайте текст, за да се появи плаващият тулбар: Expand · Shorten · Rewrite · Academic tone · Add citation · Translate. Цитатите в селекцията се запазват.',
        'За всяка секция се пази история на версиите, така че винаги можете да сравните или да се върнете назад.',
      ],
    },
    tip: {
      en: 'Smart Continue with an explicit instruction is the single most useful tool for turning a generic paragraph into your own argument.',
      bg: 'Smart Continue с ясна инструкция е най-полезният инструмент, за да превърнете общ абзац в собствен аргумент.',
    },
  },
  {
    id: 'citations',
    icon: 'Quote',
    title: { en: 'Citations: numbered Vancouver style and verification', bg: 'Цитати: номерирани по Vancouver и верификация' },
    lead: {
      en: 'InkPhD may only cite sources that are actually in the retrieved evidence. Everything else is flagged instead of silently published.',
      bg: 'InkPhD може да цитира само източници, които реално са в извлечените доказателства. Всичко останало се маркира, вместо да се публикува тихо.',
    },
    steps: {
      en: [
        'Internal citation tokens are rendered as sequential [1], [2], with [1, 2] collapsing for adjacent references and reuse of the same number for repeats.',
        'Each section ends with a numbered reference list: full title, authors, journal, year and a DOI / PubMed link.',
        'The Citation Verifier checks every emitted DOI/PMID against the retrieved evidence and reports a trust percentage; unresolved references are marked "needs review", never hidden.',
        'After adding new sources press "Re-verify" on the section so old citations are checked against the updated library.',
        'In the "Exports" tab you can reformat the whole reference list into APA 7, Vancouver, AMA 11 or BibTeX.',
      ],
      bg: [
        'Вътрешните маркери за цитат се изобразяват като последователни [1], [2], със сливане [1, 2] при съседни източници и повторно ползване на същия номер при повторение.',
        'Всяка секция завършва с номериран списък: пълно заглавие, автори, списание, година и DOI / PubMed линк.',
        'Citation Verifier проверява всеки изведен DOI/PMID срещу извлечените доказателства и дава процент на доверие; неразрешените източници се маркират „needs review“, а не се скриват.',
        'След добавяне на нови източници натиснете „Re-verify“ за секцията, за да се проверят старите цитати срещу обновената библиотека.',
        'В таб „Exports“ можете да преформатирате целия списък в APA 7, Vancouver, AMA 11 или BibTeX.',
      ],
    },
    tip: {
      en: 'Never submit a manuscript with "needs review" references. Either attach the real full text to the library or delete the claim.',
      bg: 'Никога не изпращайте ръкопис с източници „needs review“. Или прикрепете реалния пълен текст към библиотеката, или премахнете твърдението.',
    },
  },
  {
    id: 'intelligence',
    icon: 'Bot',
    title: { en: 'Copilot and the Evidence panel', bg: 'Copilot и панелът Evidence' },
    lead: {
      en: 'The right-hand Intelligence Panel has five tabs — Copilot, Evidence, Citations, Reviewer, Compliance — and always works on the section you have open.',
      bg: 'Десният панел Intelligence има пет таба — Copilot, Evidence, Citations, Reviewer, Compliance — и винаги работи върху отворената секция.',
    },
    steps: {
      en: [
        'Copilot: a multi-turn scientific chat over your own corpus. Ask "which of my sources contradict the primary outcome?" or "is my sample size defensible?" — answers carry [1], [2] citations into your library.',
        'Evidence: search your library directly, switch retrieval mode, seed the query from the open section, and read ranked excerpts with similarity scores before you write.',
        'Citations / Reviewer / Compliance: verification summary, inline AI peer review with per-issue auto-fix, and the EQUATOR checklist with missing-item generation.',
        'The three panes are resizable and the Intelligence panel can be collapsed; on a phone the workspace switches to a Chapters / Editor / AI tab switcher.',
      ],
      bg: [
        'Copilot: научен чат с история върху вашия собствен корпус. Питайте „кои от моите източници противоречат на основния изход?“ или „защитим ли е размерът на извадката?“ — отговорите носят цитати [1], [2] към библиотеката ви.',
        'Evidence: търсене директно в библиотеката, смяна на режима на извличане, автоматично пълнене на заявката от отворената секция и подредени извадки с процент сходство, преди да пишете.',
        'Citations / Reviewer / Compliance: резюме на верификацията, вграден ИИ рецензент с автоматична поправка по всеки проблем и EQUATOR чеклист с генериране на липсващите точки.',
        'Трите панела се преоразмеряват, а Intelligence панелът се свива; на телефон работната среда преминава към превключвател Chapters / Editor / AI.',
      ],
    },
    tip: {
      en: 'Use the Evidence tab before generating a difficult section: if the engine cannot find good chunks, neither will the writer.',
      bg: 'Отворете таб Evidence преди трудна секция: ако двигателят не намира добри чънкове, няма да ги намери и текстът.',
    },
  },
  {
    id: 'quality',
    icon: 'Microscope',
    title: { en: 'Quality checks: statistics, gaps, novelty, originality, grounding', bg: 'Проверки за качество: статистика, пропуски, новост, оригиналност, обоснованост' },
    lead: {
      en: 'These are the checks a reviewer will run on you. Run them first, on your own draft.',
      bg: 'Това са проверките, които рецензентът ще направи на вас. Направете ги първи — върху собствената си чернова.',
    },
    steps: {
      en: [
        'Statistical Reporting Checker (Analyze → Stats): audits 10 items — sample size, 95% CI vs p-only, multiple-testing correction, missing data, subgroups, adverse events, baseline balance, primary vs secondary outcomes, exact p-values, ITT vs per-protocol — with verbatim quotes and fixes.',
        'Research Gap Detector (Analyze → Gaps): finds what your own library covers but your text ignores, and drafts a copy-pasteable paragraph citing the relevant papers.',
        'Novelty Checker (Analyze → Novelty): scores each claim 0–10 against the library — novel / incremental / restated / contradicted.',
        'Claim Grounding (PaperQA2-style): splits the section into atomic claims and labels each supported / partial / unsupported, with the supporting excerpt and a suggested search for the gaps. One click starts a self-feedback refinement pass.',
        'Originality check: semantic similarity against your own library, paragraph by paragraph, with an adjustable threshold — a warning tool, not a plagiarism verdict.',
      ],
      bg: [
        'Statistical Reporting Checker (Analyze → Stats): проверява 10 точки — размер на извадката, 95% CI срещу само p, корекция за множествени тестове, липсващи данни, подгрупи, нежелани реакции, баланс на изходните характеристики, първичен срещу вторични изходи, точни p-стойности, ITT срещу per-protocol — с буквални цитати и предложения за поправка.',
        'Research Gap Detector (Analyze → Gaps): открива какво покрива вашата библиотека, но текстът подминава, и предлага готов за поставяне абзац с цитати към съответните статии.',
        'Novelty Checker (Analyze → Novelty): оценява всяко твърдение 0–10 срещу библиотеката — ново / постъпателно / преразказано / противоречащо.',
        'Claim Grounding (в стил PaperQA2): разделя секцията на елементарни твърдения и маркира всяко като подкрепено / частично / неподкрепено, с подкрепящата извадка и предложено търсене за пропуските. С едно кликване стартира цикъл на само-подобрение.',
        'Проверка за оригиналност: семантично сходство спрямо собствената ви библиотека, абзац по абзац, с настройваем праг — предупредителен инструмент, не присъда за плагиатство.',
      ],
    },
    tip: {
      en: 'Fix statistics findings first. They are the most common desk-reject reason and the cheapest to repair.',
      bg: 'Оправяйте първо статистическите забележки. Те са най-честата причина за отказ още на бюрото и най-лесни за поправка.',
    },
  },
  {
    id: 'readiness',
    icon: 'Gauge',
    title: { en: 'Defense Panel and Readiness Score', bg: 'Defense Panel и Readiness Score' },
    lead: {
      en: 'Two orchestrators that answer one question: is this defensible yet?',
      bg: 'Два инструмента, които отговарят на един въпрос: защитим ли е вече текстът?',
    },
    steps: {
      en: [
        'Defense Panel: five AI reviewers — Clinician, Statistician, Methodologist, Journal editor, Committee member — run in parallel and return their headline concern, likely criticisms, weak areas, unsupported claims, missing literature and a suggested defence answer you can rehearse.',
        'From a finished defence report press "Apply suggestions inline" to get 8–18 Grammarly-style accept/reject edits, each anchored to a verified quote from your text.',
        'Readiness Score: one 0–100 number per section from seven weighted signals (citation trust 15% · citation density 10% · EQUATOR 15% · statistics 20% · gap coverage 15% · novelty 15% · length 10%) plus up to 12 priority-sorted action items.',
        'Project Readiness Overview: word-weighted score for the whole manuscript, five readiness buckets and cross-section action items — click any item to jump to the section.',
      ],
      bg: [
        'Defense Panel: пет ИИ рецензента — клиницист, статистик, методолог, редактор на списание, член на комисия — работят паралелно и връщат основната си тревога, вероятни критики, слаби места, неподкрепени твърдения, липсваща литература и предложен защитен отговор за репетиция.',
        'От завършен доклад натиснете „Apply suggestions inline“, за да получите 8–18 редакции в стил Grammarly с приемане/отхвърляне, всяка закотвена към проверен цитат от текста ви.',
        'Readiness Score: едно число 0–100 на секция от седем претеглени сигнала (доверие в цитатите 15% · плътност на цитиране 10% · EQUATOR 15% · статистика 20% · покритие на пропуските 15% · новост 15% · дължина 10%) плюс до 12 приоритизирани задачи.',
        'Project Readiness Overview: претеглена по думи оценка за целия ръкопис, пет групи по готовност и задачи през всички секции — кликването върху задача отваря съответната секция.',
      ],
    },
    tip: {
      en: 'Do not chase 100. Above ~85 the remaining points usually require new data, not new writing.',
      bg: 'Не гонете 100. Над около 85 оставащите точки обикновено искат нови данни, а не нов текст.',
    },
  },
  {
    id: 'export',
    icon: 'Download',
    title: { en: 'Export and bibliography', bg: 'Експорт и библиография' },
    lead: {
      en: 'The "Exports" tab turns the workspace into a submission package.',
      bg: 'Таб „Exports“ превръща работната среда в пакет за изпращане.',
    },
    steps: {
      en: [
        'Formats: Markdown, HTML, DOCX, PDF and LaTeX (DOCX/PDF from the paid plans, LaTeX from Researcher upwards).',
        'Numbered citations and the reference list are rendered identically in every format.',
        'Choose the citation style (APA 7 / Vancouver / AMA 11 / BibTeX) before exporting; DOIs and PMIDs are resolved through Crossref and NCBI.',
        'If a Python project is attached, the export also contains a "Software" reference block.',
        'Approve each section ("human reviewed") before export — the manuscript then carries an explicit statement that a human verified the AI-assisted passages.',
      ],
      bg: [
        'Формати: Markdown, HTML, DOCX, PDF и LaTeX (DOCX/PDF от платените планове, LaTeX от Researcher нагоре).',
        'Номерираните цитати и списъкът с литература се изобразяват идентично във всеки формат.',
        'Изберете стила на цитиране (APA 7 / Vancouver / AMA 11 / BibTeX) преди експорт; DOI и PMID се разрешават през Crossref и NCBI.',
        'Ако има прикрепен Python проект, експортът съдържа и блок „Software“ с реферирането на софтуера.',
        'Одобрете всяка секция („human reviewed“) преди експорт — тогава ръкописът носи изрично заявление, че човек е проверил ИИ-подпомогнатите пасажи.',
      ],
    },
    tip: {
      en: 'Export a DOCX early and read it outside the app. Formatting problems and repetitive phrasing are far more visible there.',
      bg: 'Експортирайте DOCX рано и го прочетете извън приложението. Проблемите с форматирането и повторенията се виждат много по-ясно там.',
    },
  },
  {
    id: 'code',
    icon: 'Code2',
    title: { en: 'Article from a Python project', bg: 'Статия от Python проект' },
    lead: {
      en: 'For software and model papers: attach a repository and let InkPhD extract verifiable technical facts with file-and-line provenance.',
      bg: 'За статии за софтуер и модели: прикачете хранилище и оставете InkPhD да извлече проверими технически факти с произход до файл и ред.',
    },
    steps: {
      en: [
        '"Python project" tab → connect a GitHub repository (URL, branch/ref, token for private repos) or upload a ZIP archive.',
        'Pick the component you are writing about; relevant files are filtered and indexed into a separate code index.',
        'Extract facts: each claim is categorised and labelled code-verified, needs a literature reference, or needs a validation study. Version and commit are detected automatically.',
        'Every claim links to the real snippet — file, line range and an "Open on GitHub" deep link.',
        'Draft the Methods or System description section; markers [C#], [REF NEEDED] and [VALIDATION REQUIRED] show exactly what still needs your input. Insert it as a new section or append it to an existing one.',
        'After you push new code, "Re-sync" flags the claims that may no longer hold.',
      ],
      bg: [
        'Таб „Python project“ → свържете GitHub хранилище (URL, бранч/ref, токен за частни хранилища) или качете ZIP архив.',
        'Изберете компонента, за който пишете; релевантните файлове се филтрират и индексират в отделен код индекс.',
        'Извлечете факти: всяко твърдение се категоризира и получава етикет — проверено в кода, нужна е литературна референция или нужно е валидационно изследване. Версията и комитът се откриват автоматично.',
        'Всяко твърдение сочи към реалния сниппет — файл, диапазон от редове и директен линк „Open on GitHub“.',
        'Генерирайте секцията „Методи“ или „Описание на системата“; маркерите [C#], [REF NEEDED] и [VALIDATION REQUIRED] показват точно какво още изисква вашата намеса. Вмъкнете я като нова секция или добавете към съществуваща.',
        'След като качите нов код, „Re-sync“ маркира твърденията, които може вече да не са верни.',
      ],
    },
    tip: {
      en: 'Never let a performance number reach the manuscript without a validation study behind it — the module deliberately refuses to invent metrics.',
      bg: 'Никога не допускайте число за производителност в ръкописа без валидационно изследване зад него — модулът умишлено отказва да измисля метрики.',
    },
  },
  {
    id: 'rules',
    icon: 'ShieldCheck',
    title: { en: 'Rules of good practice (read this once)', bg: 'Правила за добра практика (прочетете това веднъж)' },
    lead: {
      en: 'InkPhD is infrastructure, not an author. Journals and universities hold you responsible for every sentence.',
      bg: 'InkPhD е инфраструктура, не автор. Списанията и университетите държат вас отговорни за всяко изречение.',
    },
    steps: {
      en: [
        'Read every generated paragraph against the cited source. If you cannot defend the sentence in a viva, rewrite or delete it.',
        'AI is not an author (ICMJE). Disclose AI assistance the way your journal requires — InkPhD keeps the per-section metadata you need for that statement.',
        'No patient-identifying data in prompts or uploads; the platform is research-use-only and is not a medical device.',
        'Your manuscripts and library are never used to train models. You can export or erase everything at any time from Profile → Data & privacy.',
        'Keep your own backup: export a DOCX or Markdown snapshot at every milestone.',
      ],
      bg: [
        'Четете всеки генериран абзац срещу цитирания източник. Ако не можете да защитите изречението на защита, пренапишете го или го изтрийте.',
        'ИИ не е автор (ICMJE). Декларирайте ИИ помощта така, както изисква вашето списание — InkPhD пази метаданните по секции, нужни за това заявление.',
        'Никакви данни, идентифициращи пациенти, в промптите или качените файлове; платформата е с изследователско предназначение и не е медицинско изделие.',
        'Вашите ръкописи и библиотека никога не се ползват за обучение на модели. Можете да експортирате или изтриете всичко по всяко време от Профил → Данни и поверителност.',
        'Пазете собствен архив: експортирайте DOCX или Markdown на всеки важен етап.',
      ],
    },
    tip: {
      en: 'The strongest workflow is boring: small sections, good sources, verify, approve, export. Repeat.',
      bg: 'Най-силният работен процес е скучен: малки секции, добри източници, проверка, одобрение, експорт. И отново.',
    },
  },
];
